import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Building2,
  Globe,
  Mail,
  Linkedin,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Search,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Users,
  Target,
  ArrowLeft,
  RefreshCw,
  Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchCompanies();
    fetchStats();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies?limit=100`);
      setCompanies(response.data.companies);
    } catch (error) {
      toast.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/companies/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const viewCompanyDetails = async (companyId) => {
    try {
      const response = await axios.get(`${API}/companies/${companyId}`);
      setSelectedCompany(response.data);
      setDetailsOpen(true);
    } catch (error) {
      toast.error("Failed to fetch company details");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-emerald-600 bg-emerald-50";
    if (score >= 40) return "text-amber-600 bg-amber-50";
    return "text-red-600 bg-red-50";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 70) return "Strong";
    if (score >= 60) return "Good";
    if (score >= 40) return "Average";
    return "Needs Work";
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const SocialIcon = ({ platform, url }) => {
    if (!url) return null;
    const icons = {
      linkedin: Linkedin,
      twitter: Twitter,
      facebook: Facebook,
      instagram: Instagram,
      youtube: Youtube,
    };
    const Icon = icons[platform];
    if (!Icon) return null;
    
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-slate-400 hover:text-slate-600 transition-colors"
      >
        <Icon className="w-4 h-4" />
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                Company Database
              </h1>
              <p className="text-sm text-slate-500">
                All analyzed companies with enriched data
              </p>
            </div>
            <Button onClick={fetchCompanies} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                  <div className="text-xs text-slate-500">Total Companies</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Linkedin className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{stats.with_linkedin}</div>
                  <div className="text-xs text-slate-500">With LinkedIn</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{stats.with_email}</div>
                  <div className="text-xs text-slate-500">With Email</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{stats.with_social}</div>
                  <div className="text-xs text-slate-500">Social Presence</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="company-search-input"
            />
          </div>
        </div>

        {/* Companies Table */}
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Social</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>LinkedIn Info</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Loading companies...
                  </TableCell>
                </TableRow>
              ) : filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No companies found. Run an analysis to start building your database.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow key={company.id} data-testid={`company-row-${company.domain}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 max-w-[200px] truncate">
                            {company.company_name || company.domain}
                          </div>
                          <a
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-slate-500 hover:text-emerald-600 flex items-center gap-1"
                          >
                            {company.domain}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {company.latest_score !== undefined ? (
                        <Badge className={getScoreColor(company.latest_score)}>
                          {company.latest_score}/100 • {getScoreLabel(company.latest_score)}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <SocialIcon platform="linkedin" url={company.linkedin_url} />
                        <SocialIcon platform="twitter" url={company.twitter_url} />
                        <SocialIcon platform="facebook" url={company.facebook_url} />
                        <SocialIcon platform="instagram" url={company.instagram_url} />
                        <SocialIcon platform="youtube" url={company.youtube_url} />
                      </div>
                    </TableCell>
                    <TableCell>
                      {company.primary_email ? (
                        <a
                          href={`mailto:${company.primary_email}`}
                          className="text-sm text-emerald-600 hover:underline"
                        >
                          {company.primary_email}
                        </a>
                      ) : company.contact_emails?.length > 0 ? (
                        <span className="text-sm text-slate-600">
                          {company.contact_emails[0]}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">No email</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.linkedin_data ? (
                        <div className="text-xs">
                          {company.linkedin_data.company_size && (
                            <div className="text-slate-600">
                              <Users className="w-3 h-3 inline mr-1" />
                              {company.linkedin_data.company_size} employees
                            </div>
                          )}
                          {company.linkedin_data.followers && (
                            <div className="text-slate-500">
                              {company.linkedin_data.followers} followers
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewCompanyDetails(company.id)}
                        data-testid={`view-company-${company.domain}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </main>

      {/* Company Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedCompany && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                  {selectedCompany.company_name || selectedCompany.domain}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Score Overview */}
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className={`text-4xl font-bold ${selectedCompany.latest_score >= 70 ? "text-emerald-600" : selectedCompany.latest_score >= 40 ? "text-amber-600" : "text-red-600"}`}>
                      {selectedCompany.latest_score || "-"}
                    </div>
                    <div className="text-sm text-slate-500">GTM Score</div>
                  </div>
                  <div className="flex-1">
                    <Progress
                      value={selectedCompany.latest_score || 0}
                      className="h-3"
                    />
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">Website</label>
                    <a
                      href={selectedCompany.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-emerald-600 hover:underline"
                    >
                      {selectedCompany.website}
                    </a>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">Domain</label>
                    <div className="text-slate-900">{selectedCompany.domain}</div>
                  </div>
                </div>

                {/* Contact Emails */}
                {selectedCompany.contact_emails?.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase mb-2 block">
                      Contact Emails
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {selectedCompany.contact_emails.map((email, idx) => (
                        <Badge key={idx} variant="outline" className="bg-white">
                          <Mail className="w-3 h-3 mr-1" />
                          {email}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Profiles */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase mb-2 block">
                    Social Profiles
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {selectedCompany.linkedin_url && (
                      <a
                        href={selectedCompany.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                    )}
                    {selectedCompany.twitter_url && (
                      <a
                        href={selectedCompany.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-sky-50 text-sky-700 rounded-lg hover:bg-sky-100 transition-colors"
                      >
                        <Twitter className="w-4 h-4" />
                        Twitter
                      </a>
                    )}
                    {selectedCompany.facebook_url && (
                      <a
                        href={selectedCompany.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                    )}
                    {selectedCompany.instagram_url && (
                      <a
                        href={selectedCompany.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors"
                      >
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </a>
                    )}
                    {selectedCompany.youtube_url && (
                      <a
                        href={selectedCompany.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <Youtube className="w-4 h-4" />
                        YouTube
                      </a>
                    )}
                  </div>
                </div>

                {/* LinkedIn Data */}
                {selectedCompany.linkedin_data && Object.keys(selectedCompany.linkedin_data).length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase mb-2 block">
                      LinkedIn Intelligence
                    </label>
                    <Card className="p-4 bg-blue-50/50">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedCompany.linkedin_data.company_name && (
                          <div>
                            <span className="text-slate-500">Company:</span>{" "}
                            <span className="font-medium">{selectedCompany.linkedin_data.company_name}</span>
                          </div>
                        )}
                        {selectedCompany.linkedin_data.industry && (
                          <div>
                            <span className="text-slate-500">Industry:</span>{" "}
                            <span className="font-medium">{selectedCompany.linkedin_data.industry}</span>
                          </div>
                        )}
                        {selectedCompany.linkedin_data.company_size && (
                          <div>
                            <span className="text-slate-500">Size:</span>{" "}
                            <span className="font-medium">{selectedCompany.linkedin_data.company_size} employees</span>
                          </div>
                        )}
                        {selectedCompany.linkedin_data.followers && (
                          <div>
                            <span className="text-slate-500">Followers:</span>{" "}
                            <span className="font-medium">{selectedCompany.linkedin_data.followers}</span>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                )}

                {/* Strengths & Gaps */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedCompany.top_strengths?.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase mb-2 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                        Top Strengths
                      </label>
                      <ul className="space-y-2">
                        {selectedCompany.top_strengths.slice(0, 3).map((strength, idx) => (
                          <li key={idx} className="text-sm text-slate-600 bg-emerald-50 p-2 rounded">
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedCompany.top_gaps?.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase mb-2 flex items-center gap-1">
                        <TrendingDown className="w-3 h-3 text-amber-500" />
                        Top Gaps
                      </label>
                      <ul className="space-y-2">
                        {selectedCompany.top_gaps.slice(0, 3).map((gap, idx) => (
                          <li key={idx} className="text-sm text-slate-600 bg-amber-50 p-2 rounded">
                            {gap}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Summary */}
                {selectedCompany.company_summary && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase mb-2 block">
                      Company Summary
                    </label>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {selectedCompany.company_summary}
                    </p>
                  </div>
                )}

                {/* View Reports */}
                {selectedCompany.deliverables?.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase mb-2 block">
                      Analysis Reports ({selectedCompany.deliverables.length})
                    </label>
                    <div className="space-y-2">
                      {selectedCompany.deliverables.map((d, idx) => (
                        <Link
                          key={d.id}
                          to={`/results/${d.id}`}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <span className="text-sm text-slate-700">
                            Report #{idx + 1} • Score: {d.scores?.final_score || "-"}
                          </span>
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
